import { useCallback, useMemo, useState } from "react";
import { Pencil, Plus, Trash2 } from "lucide-react";

import AllergyEmptyState from "assets/images/alleries-empty-state.svg";
import { Button } from "components/ui/Button";
import { ConfirmDialog } from "components/ui/ConfirmDialog";
import { DataTable } from "components/ui/DataTable";
import { Pill, PILL_COLORS } from "components/ui/Pill";
import { AllergyFormModal } from "features/patients/components/AllergyFormModal/AllergyFormModal";
import { useAllergyMutations } from "features/patients/hooks/useAllergyMutations";
import { useCurrentPatientId } from "features/patients/hooks/useCurrentPatientId";
import { usePatientAllergies } from "features/patients/hooks/usePatientAllergies";

import s from "./Allergies.module.css";

const formatLabel = (value) => {
  if (!value) return "-";
  return value.charAt(0).toUpperCase() + value.slice(1).toLowerCase();
};

const Allergies = () => {
  const patientId = useCurrentPatientId();

  const [isModalOpen, setIsModalOpen] = useState({ open: false, id: null });
  const [allergyToDisable, setAllergyToDisable] = useState(null);
  const [filters, setFilters] = useState({});

  const { data = [], isLoading } = usePatientAllergies(patientId);
  const { disableAllergy } = useAllergyMutations(patientId);

  const filteredData = useMemo(
    () =>
      filters.showDisabled === "true"
        ? data
        : data.filter((item) => !item.disabled),
    [data, filters.showDisabled],
  );

  const filtersConfig = [
    {
      id: "showDisabled",
      label: "",
      type: "single",
      options: [{ value: "true", label: "Show deleted" }],
    },
  ];

  const handleOpenModal = useCallback((id) => {
    setIsModalOpen({ open: true, id: typeof id === "number" ? id : null });
  }, []);

  const columns = useMemo(
    () => [
      {
        id: "name",
        header: "Name",
        // 140px: the four columns plus the row actions were 10px past the card, which
        // put a horizontal scrollbar under a table that otherwise fits. The name
        // truncates with a tooltip either way, so the width comes from here.
        width: "140px",
        render: (item) => item.name || "-",
        getTooltipText: (item) => item.name || "-",
      },
      {
        id: "allergyReaction",
        header: "Symptoms",
        width: "minmax(105px, 1fr)",
        render: (item) => {
          if (!item.allergyReaction) return "-";
          const c = PILL_COLORS.reactionUnknown;
          return (
            <Pill color={c.color} bg={c.bg} border={c.border}>
              {formatLabel(item.allergyReaction)}
            </Pill>
          );
        },
      },
      {
        id: "drugAllergy",
        header: "Drug Allergy",
        width: "100px",
        render: (item) => {
          const c = PILL_COLORS.reactionUnknown;
          return (
            <Pill color={c.color} bg={c.bg} border={c.border}>
              {item.drugAllergy ? "Yes" : "No"}
            </Pill>
          );
        },
      },
      {
        id: "serious",
        header: "Serious",
        width: "60px",
        render: (item) => {
          const c = item.serious
            ? PILL_COLORS.reactionYes
            : PILL_COLORS.reactionUnknown;
          return (
            <Pill color={c.color} bg={c.bg} border={c.border}>
              {item.serious ? "Yes" : "No"}
            </Pill>
          );
        },
      },
    ],
    [],
  );

  const rowActions = useMemo(
    () => [
      {
        label: "Edit",
        icon: Pencil,
        primary: true,
        onClick: (item) => handleOpenModal(item.id),
      },
      {
        label: "Delete",
        icon: Trash2,
        primary: true,
        visibleWhen: (item) => !item.disabled,
        onClick: (item) => setAllergyToDisable(item),
      },
    ],
    [handleOpenModal],
  );

  return (
    <div className={s.allergyWrapper}>
      <div className={s.topWrapper}>
        <h3 className={s.title}>Allergies</h3>
        <Button variant="text" onClick={handleOpenModal} endIcon={Plus}>
          Add new
        </Button>
      </div>
      <DataTable
        columns={columns}
        data={filteredData}
        getRowId={(row) => row.id}
        isLoading={isLoading}
        emptyIllustration={AllergyEmptyState}
        emptyTitle="No Known Allergies"
        emptyText="Once a doctor adds an allergy, it will appear here."
        rowActions={rowActions}
        getRowClassName={(row) => (row.disabled ? "disabled" : undefined)}
        rowsPerPage={5}
        rowsOptions={[5, 10, 20, 50, 100]}
        filtersConfig={filtersConfig}
        filters={{
          value: filters,
          onChange: (groupId, value) =>
            setFilters((prev) => ({ ...prev, [groupId]: value })),
        }}
      />

      <AllergyFormModal
        isOpen={isModalOpen.open}
        editId={isModalOpen.id}
        data={data}
        patientId={patientId}
        onClose={() => setIsModalOpen({ open: false, id: null })}
      />

      <ConfirmDialog
        isOpen={!!allergyToDisable}
        onClose={() => setAllergyToDisable(null)}
        onConfirm={() => {
          if (allergyToDisable) {
            disableAllergy.mutate(allergyToDisable.id, {
              onSuccess: () => setAllergyToDisable(null),
            });
          }
        }}
        isLoading={disableAllergy.isPending}
        title="Delete allergy"
        message="Are you sure you want to delete this allergy? This action cannot be undone."
        confirmLabel="Delete"
      />
    </div>
  );
};

export { Allergies };
